// supervisor-release-approved-g3-smoke-step.ts
//
// The smoke/verify tail of the "G3 approved" leg — split out of
// supervisor-release-approved-g3-leg.ts (200-line budget) when phase 04b gave
// the leg its env sources. Behaviour moved unchanged, except the env:
// `env_allowlist ∩ releaseRecipeEnv(...)` (spec V2 §4.2, B22 — smoke
// gets the stdin map exactly like deploy), built right before the spawn so
// `test.env` is re-read and its values are known-values before output exists.
// Review 04b H1: env = stdin > shell, `test.env` only without stdin (merger).
// L4: output redaction keyed by `missionId`, like the merger — not by control root.
import { isDelegatedG3Grant } from "./delegated-g3-release-secrets-policy.js";
import { knownValuesFor } from "./mission-secrets-test-env-loader.js";
import { resolveRecipeCwdAgainstRepoRoot } from "./recipe-cwd-resolved-against-repo-root.js";
import { releaseRecipeEnv } from "./recipe-env-source-merger.js";
import { recordSmokeReceipt } from "./release-deploy-completion-receipt.js";
import type { ReleaseConfig } from "./release-manifest-hash.js";
import { recipeTimeoutMs } from "./release-recipe-run-inputs.js";
import type { ApprovedG3LegInput } from "./supervisor-release-approved-g3-leg.js";
import { runReleaseSmokeStep } from "./supervisor-release-deploy-verify.js";
import type { ReleaseFlowResumeOutcome } from "./supervisor-release-flow-resume-outcome.js";

export async function runApprovedG3SmokeThenRecordReceipt(
  input: ApprovedG3LegInput,
  smokeRecipe: NonNullable<ReleaseConfig["verify_recipe"]>,
): Promise<ReleaseFlowResumeOutcome> {
  const env = await releaseRecipeEnv(
    input.missionId,
    smokeRecipe.env_allowlist,
    input.releaseSecretsStdin,
    { recipeLabel: "smoke", stdinOnly: isDelegatedG3Grant(input.g3Source) },
  );
  const smoke = await runReleaseSmokeStep({
    store: input.store,
    candidateSha: input.candidateSha,
    verify: {
      smokeRecipe: resolveRecipeCwdAgainstRepoRoot(smokeRecipe, input.repoDir),
      env,
      timeoutMs: recipeTimeoutMs(input.release.verify_timeout_sec),
      secretValues: await knownValuesFor(input.missionId), // 04a L5 + 04b L4: output
    },
  });
  if (smoke.status !== "verified") return { kind: "deploy_smoke_failed", reason: smoke.reason };
  await recordSmokeReceipt(input.controlRoot, {
    candidateSha: input.candidateSha,
    releaseManifestHash: input.releaseManifestHash,
    smokeRecipeId: smokeRecipe.recipe_id,
  });
  return { kind: "released" };
}
